import { useCallback, useEffect } from 'react'

import changeSet from 'core/change_set'

import {
  LEGACY_SECTION_REWRITES,
  SETTINGS_MOBILE_LIST_SENTINEL,
  mergeSettingsSearch,
  parseSettingsTabFromSearch,
  resolveEffectiveSettingsTab,
  stripSettingsSearch,
} from './constants'

/**
 * Confirm there are no unsaved changes (or that the user accepted discarding them) before
 * navigating away from the current settings section. Centralised here so every URL writer in this
 * hook (and `replaceSettingsSection`) goes through the same prompt path.
 */
export function confirmDiscardIfDirty() {
  if (!changeSet.isDirty()) {
    return true
  }
  return changeSet.confirmReset()
}

/**
 * `history.replace` helper that swaps the `?settings=` query for `section` (or strips it). Pulled
 * out of `<SettingsModal>` so {@link useSettingsModalUrlNormalization}'s effects and callbacks can
 * share a single canonical implementation.
 *
 * @param {import('react-router').History} history
 * @param {import('react-router').Location} location
 * @param {string | null} section — `SETTINGS_SECTION` value (or `null` to close the modal)
 */
export function replaceSettingsSection(history, location, section) {
  if (section == null) {
    history.replace({
      pathname: location.pathname,
      search: stripSettingsSearch(location.search),
      hash: location.hash,
    })
    return
  }
  history.replace({
    pathname: location.pathname,
    search: mergeSettingsSearch(section, location.search),
    hash: location.hash,
  })
}

/**
 * Owns URL-rewriting side effects for the settings modal — legacy `?settings=` aliases and the
 * "fold unknown / unauthorised section back to Profile" guard. Legacy pathname migration on load
 * lives in {@link ./legacy_settings_host_redirect.js}. Returns the user-facing nav callbacks
 * (`closeModal`, `goToSection`) so callers never need to import `confirmDiscardIfDirty` directly.
 *
 * @param {object} args
 * @param {boolean} args.isSettingsModalEnabled — `ap_rebrand_settings_ia` flag
 * @param {boolean} args.isOpen — flag is on AND `?settings=` is present
 * @param {string | null} args.rawSettingsSection — pre-rewrite `?settings=` value
 * @param {string | null} args.section — post-rewrite section id (alias-aware)
 * @param {string} args.effectiveDesktopSection — section the modal will actually render
 * @param {import('react-router').History} args.history
 * @param {import('react-router').Location} args.location
 * @returns {{
 *   closeModal: () => void,
 *   goToSection: (next: string | null) => void,
 * }}
 */
export function useSettingsModalUrlNormalization({
  isSettingsModalEnabled,
  isOpen,
  rawSettingsSection,
  section,
  effectiveDesktopSection,
  history,
  location,
}) {
  /**
   * Rewrite `?settings=<legacy-alias>` to the canonical section + tab. Runs once per
   * `location.search` change; subsequent renders see the post-rewrite values.
   */
  useEffect(() => {
    if (!isSettingsModalEnabled || !isOpen) {
      return
    }
    if (rawSettingsSection == null) {
      return
    }
    const rule = LEGACY_SECTION_REWRITES[rawSettingsSection]
    if (rule == null) {
      return
    }
    const targetTab = resolveEffectiveSettingsTab(
      rawSettingsSection,
      parseSettingsTabFromSearch(location.search),
    )
    history.replace({
      pathname: location.pathname,
      search: mergeSettingsSearch(rule.section, location.search, targetTab ?? undefined),
      hash: location.hash,
    })
  }, [
    history,
    isOpen,
    isSettingsModalEnabled,
    location.hash,
    location.pathname,
    location.search,
    rawSettingsSection,
  ])

  /**
   * If the URL points at an unknown / unauthorised section, write the fallback (Profile) back to
   * the URL so refresh / share preserves what the user is actually seeing.
   */
  useEffect(() => {
    if (!isSettingsModalEnabled || !isOpen) {
      return
    }
    if (section == null || section === SETTINGS_MOBILE_LIST_SENTINEL) {
      return
    }
    if (section !== effectiveDesktopSection) {
      replaceSettingsSection(history, location, effectiveDesktopSection)
    }
  }, [effectiveDesktopSection, history, isOpen, isSettingsModalEnabled, location, section])

  const closeModal = useCallback(() => {
    if (!confirmDiscardIfDirty()) {
      return
    }
    history.replace({
      pathname: location.pathname,
      search: stripSettingsSearch(location.search),
      hash: location.hash,
    })
  }, [history, location.hash, location.pathname, location.search])

  const goToSection = useCallback((next) => {
    if (!confirmDiscardIfDirty()) {
      return
    }
    replaceSettingsSection(history, location, next)
  }, [history, location])

  return { closeModal, goToSection }
}
